import { useState } from 'react'
import { api, errorMessage, useQuery } from '../api.js'
import { Alert, Button, Field, Input, Panel, Pill, PowerPill } from '../components/ui.js'
import { StepUpModal, type StepUpRequest } from '../components/StepUpModal.js'

type Server = Awaited<ReturnType<typeof api.fleet.servers.query>>[number]

export function Fleet() {
  const [stepUp, setStepUp] = useState<StepUpRequest | null>(null)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'crit'; text: string } | null>(null)
  const [addServer, setAddServer] = useState(false)
  const [powerId, setPowerId] = useState<string>('')

  // Polling de 10 s: durante uma transicao de energia o estado muda sozinho,
  // e ficar apertando F5 num painel de infraestrutura e sinal de painel ruim.
  const servers = useQuery(() => api.fleet.servers.query(), [], { pollMs: 10_000 })
  const providers = useQuery(() => api.fleet.providers.query(), [], { pollMs: 30_000 })

  /**
   * Monta a frase de impacto a partir do catalogo.
   *
   * Ela entra na confirmacao ANTES de a pessoa digitar o hostname. Descobrir
   * pelo telefone que o Portal caiu junto e o custo de nao ter isto aqui — e o
   * catalogo ja sabe a resposta.
   */
  async function frasePodeCair(serverId: string): Promise<string> {
    try {
      const i = await api.catalog.serverImpact.query({ serverId })
      if (!i.diretos.length && !i.emCascata.length) {
        return 'Nenhum produto do catalogo esta amarrado a este servidor — o que nao garante que nao haja nada rodando nele, so que ninguem cadastrou.'
      }
      const partes: string[] = []
      if (i.diretos.length) {
        partes.push(
          'Cai junto: ' +
            i.diretos.map((d) => `${d.nome} (${d.ambiente})`).join(', ') +
            '.',
        )
      }
      if (i.emCascata.length) {
        partes.push('Em cascata: ' + i.emCascata.map((c) => c.nome).join(', ') + '.')
      }
      return partes.join(' ')
    } catch {
      // O catalogo nao pode impedir uma acao de energia. Sem ele, segue sem a
      // frase — mas dizendo que seguiu sem.
      return 'Nao foi possivel consultar o catalogo agora; siga sabendo que o impacto nao foi conferido.'
    }
  }

  async function act(
    server: Server,
    action: 'on' | 'off' | 'restart',
  ) {
    const labels = { on: 'Ligar', off: 'Desligar', restart: 'Reiniciar' }
    const impacto = action === 'on' ? '' : ` ${await frasePodeCair(server.id)}`
    setStepUp({
      action: `server.power.${action}`,
      title: `${labels[action]} ${server.hostname}`,
      description:
        (action === 'on'
          ? 'A maquina sera ligada pelo provedor.'
          : action === 'off'
            ? 'A maquina sera desligada. Tudo que roda nela fica indisponivel ate ser ligada de novo.'
            : 'A maquina sera reiniciada. Servicos ficam indisponiveis durante o boot.') + impacto,
      confirmHostname: action === 'on' ? undefined : server.hostname,
      onProved: async (proofId) => {
        try {
          const res =
            action === 'on'
              ? await api.fleet.powerOn.mutate({ serverId: server.id, stepUpProofId: proofId })
              : action === 'off'
                ? await api.fleet.powerOff.mutate({
                    serverId: server.id,
                    stepUpProofId: proofId,
                    confirmHostname: server.hostname,
                  })
                : await api.fleet.powerRestart.mutate({
                    serverId: server.id,
                    stepUpProofId: proofId,
                    confirmHostname: server.hostname,
                  })
          setNotice({ tone: res.route === 'manual' ? 'warn' : 'ok', text: res.message })
          void servers.refetch()
        } catch (err) {
          setNotice({ tone: 'crit', text: errorMessage(err) })
        }
      },
    })
  }

  /**
   * Excluir um servidor cadastrado por engano.
   *
   * So aparece para servidor manual (sem provedor): a regra de negocio recusa
   * os que vem de provedor, porque a sincronizacao os traria de volta. Nao
   * desliga a maquina — apaga o cadastro e o que o painel guardava dele.
   */
  function del(server: Server) {
    setStepUp({
      action: 'server.delete',
      title: `Excluir ${server.hostname}`,
      description:
        'Remove este servidor do Via 360 e tudo que o painel guardava dele: contas lidas, metricas, historico de comandos. Nao desliga a maquina nem mexe no que roda nela — so apaga o cadastro. Nao tem volta.',
      confirmHostname: server.hostname,
      requireReason: true,
      onProved: async (proofId) => {
        try {
          await api.fleet.deleteServer.mutate({
            serverId: server.id,
            stepUpProofId: proofId,
            confirmHostname: server.hostname,
          })
          setNotice({ tone: 'ok', text: `Servidor ${server.hostname} excluido do Via 360.` })
          void servers.refetch()
        } catch (err) {
          setNotice({ tone: 'crit', text: errorMessage(err) })
        }
      },
    })
  }

  const lista = servers.data ?? []
  const powerAlvo = lista.find((s) => s.id === powerId) ?? lista[0]

  return (
    <div className="space-y-6">
      {notice && (
        <Alert tone={notice.tone}>
          <div className="flex items-start justify-between gap-4">
            <span>{notice.text}</span>
            <button onClick={() => setNotice(null)} className="shrink-0 text-xs underline">
              fechar
            </button>
          </div>
        </Alert>
      )}

      <Panel
        title="Provedores"
        action={
          <Button
            variant="ghost"
            onClick={async () => {
              for (const p of providers.data ?? []) {
                try {
                  await api.fleet.syncProvider.mutate({ providerId: p.id })
                } catch (err) {
                  setNotice({ tone: 'crit', text: errorMessage(err) })
                }
              }
              void servers.refetch()
              void providers.refetch()
            }}
          >
            Sincronizar agora
          </Button>
        }
      >
        {providers.data?.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {providers.data.map((p) => (
              <div key={p.id} className="rounded border border-line p-4">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{p.label}</span>
                  <Pill tone={p.status === 'ok' ? 'ok' : 'crit'}>{p.status}</Pill>
                </div>
                <p className="mt-1 font-mono text-xs text-ink-3">{p.kind}</p>
                <p className="mt-3 font-mono text-[11px] text-ink-3">
                  {/* A matriz de capacidades e o coracao da Parte I: mostrar o
                      que cada API sabe fazer evita prometer botao que nao age. */}
                  ligar {p.capabilities.powerOn ? '✓' : '✗'} · desligar{' '}
                  {p.capabilities.powerOff ? '✓' : '✗'} · reiniciar{' '}
                  {p.capabilities.powerRestart ? '✓' : '✗'} · snapshot{' '}
                  {p.capabilities.createSnapshot ? '✓' : '✗'}
                </p>
                {p.lastError && <p className="mt-2 text-xs text-crit">{p.lastError}</p>}
                <p className="mt-2 text-[11px] text-ink-3">
                  {p.lastSyncAt
                    ? `sincronizado ${new Date(p.lastSyncAt).toLocaleString('pt-BR')}`
                    : 'nunca sincronizado'}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-ink-3">
            Nenhum provedor cadastrado ainda. Adicione Kamatera, Locaweb ou Hostinger para o
            inventario aparecer aqui.
          </p>
        )}
      </Panel>

      <Panel title="Energia">
        {lista.length === 0 ? (
          <p className="text-sm text-ink-3">
            Nenhum servidor para controlar ainda. Cadastre um provedor ou adicione um servidor.
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Servidor">
              <select
                value={powerAlvo?.id ?? ''}
                onChange={(e) => setPowerId(e.target.value)}
                className="w-full min-w-[16rem] rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
              >
                {lista.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.hostname}
                    {s.publicIp ? ` — ${s.publicIp}` : ''} · {s.powerState}
                  </option>
                ))}
              </select>
            </Field>
            <div className="flex gap-1.5">
              <Button
                onClick={() => powerAlvo && act(powerAlvo, 'on')}
                disabled={!powerAlvo?.can.on || powerAlvo?.powerState === 'on'}
                title={powerAlvo?.can.on ? 'Ligar' : 'A API deste provedor nao expoe "ligar"'}
              >
                Ligar
              </Button>
              <Button
                onClick={() => powerAlvo && act(powerAlvo, 'restart')}
                disabled={!powerAlvo?.can.restart}
                title={powerAlvo?.can.restart ? 'Reiniciar' : 'Nao suportado pela API deste provedor'}
              >
                Reiniciar
              </Button>
              <Button
                variant="danger"
                onClick={() => powerAlvo && act(powerAlvo, 'off')}
                disabled={!powerAlvo?.can.off || powerAlvo?.powerState === 'off'}
                title={powerAlvo?.can.off ? 'Desligar' : 'Nao suportado pela API deste provedor'}
              >
                Desligar
              </Button>
            </div>
            {powerAlvo && !powerAlvo.can.on && !powerAlvo.can.off && !powerAlvo.can.restart && (
              <p className="w-full text-xs text-ink-3">
                Este servidor nao tem provedor com API de energia — ligar/desligar por aqui nao se
                aplica. Reiniciar pelo agente entra numa proxima fase.
              </p>
            )}
          </div>
        )}
      </Panel>

      <Panel
        title={`Frota — ${servers.data?.length ?? 0} servidores`}
        action={
          <Button variant="primary" onClick={() => setAddServer(true)}>
            Adicionar servidor
          </Button>
        }
      >
        {servers.error && <Alert>{servers.error}</Alert>}
        {servers.loading && !servers.data && <p className="text-sm text-ink-3">Carregando...</p>}
        {!servers.loading && (servers.data?.length ?? 0) === 0 && (
          <p className="mb-3 text-sm text-ink-3">
            Nenhum servidor ainda. Cadastre um provedor acima e clique em “Sincronizar agora”, ou
            use “Adicionar servidor” para um VPS que nao vem por API de provedor (como o proprio
            servidor do painel).
          </p>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left font-mono text-[10px] uppercase tracking-[0.12em] text-ink-3">
                <th className="py-2 pr-4">Servidor</th>
                <th className="py-2 pr-4">Provedor</th>
                <th className="py-2 pr-4">SO</th>
                <th className="py-2 pr-4">IP</th>
                <th className="py-2 pr-4">Ambiente</th>
                <th className="py-2 pr-4">Estado</th>
                <th className="py-2 pr-4">Agente</th>
                <th className="py-2">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {(servers.data ?? []).map((s) => (
                <tr key={s.id} className="border-b border-line last:border-0">
                  <td className="py-3 pr-4 font-medium">{s.hostname}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-ink-3">{s.provider?.kind}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-ink-3">
                    {s.os === 'unknown' ? '—' : s.os}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-ink-3">{s.publicIp ?? '—'}</td>
                  <td className="py-3 pr-4 text-xs text-ink-3">{s.environment}</td>
                  <td className="py-3 pr-4">
                    <PowerPill state={s.powerState} />
                  </td>
                  <td className="py-3 pr-4">
                    <Pill tone={s.managedState === 'managed' ? 'ok' : s.managedState === 'orphaned' ? 'crit' : 'warn'}>
                      {s.managedState === 'managed'
                        ? 'com agente'
                        : s.managedState === 'orphaned'
                          ? 'orfao'
                          : 'sem agente'}
                    </Pill>
                  </td>
                  <td className="py-3">
                    <div className="flex gap-1.5">
                      <Button
                        onClick={() => act(s, 'on')}
                        disabled={!s.can.on || s.powerState === 'on'}
                        title={s.can.on ? 'Ligar' : 'A API deste provedor nao expoe "ligar"'}
                      >
                        Ligar
                      </Button>
                      <Button
                        onClick={() => act(s, 'restart')}
                        disabled={!s.can.restart}
                        title={s.can.restart ? 'Reiniciar' : 'Nao suportado pela API — cai para o agente na Fase 2'}
                      >
                        Reiniciar
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => act(s, 'off')}
                        disabled={!s.can.off || s.powerState === 'off'}
                        title={s.can.off ? 'Desligar' : 'Nao suportado pela API — cai para o agente na Fase 2'}
                      >
                        Desligar
                      </Button>
                      {!s.provider && (
                        <Button
                          variant="ghost"
                          onClick={() => del(s)}
                          title="Excluir este servidor do Via 360 (cadastro manual)"
                        >
                          excluir
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {servers.data?.length === 0 && !servers.loading && (
          <p className="mt-4 text-sm text-ink-3">
            Nenhum servidor no inventario. Cadastre um provedor e clique em “Sincronizar agora”.
          </p>
        )}
      </Panel>

      {stepUp && <StepUpModal request={stepUp} onClose={() => setStepUp(null)} />}
      {addServer && (
        <AddServerModal
          onClose={() => setAddServer(false)}
          onAdded={() => {
            setAddServer(false)
            void servers.refetch()
            setNotice({ tone: 'ok', text: 'Servidor cadastrado. Instale o agente nele pela aba Contas.' })
          }}
        />
      )}
    </div>
  )
}


/**
 * Cadastro de servidor a mao.
 *
 * Para um VPS que nao vem por API de provedor — o proprio servidor do painel,
 * um host avulso. Depois de cadastrado, instala-se o agente nele pela aba
 * Contas, e dali as contas e o monitor funcionam como em qualquer servidor.
 * Ligar/desligar por API nao existe aqui (nao ha provedor), e a tela nao
 * oferece esses botoes para nao prometer o que nao pode cumprir.
 */
function AddServerModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [hostname, setHostname] = useState('')
  const [os, setOs] = useState<'windows' | 'linux' | 'unknown'>('linux')
  const [publicIp, setPublicIp] = useState('')
  const [environment, setEnvironment] = useState<'producao' | 'homologacao' | 'desenvolvimento' | 'outro'>('producao')
  const [erro, setErro] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function salvar() {
    setBusy(true)
    setErro(null)
    try {
      await api.fleet.addServer.mutate({
        hostname: hostname.trim(),
        os,
        publicIp: publicIp.trim() || undefined,
        environment,
      })
      onAdded()
    } catch (err) {
      setErro(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/45 p-4">
      <div className="w-full max-w-md rounded border border-line bg-panel p-6 shadow-xl">
        <h3 className="text-lg font-semibold tracking-tight">Adicionar servidor</h3>
        <p className="mt-1 text-sm text-ink-2">
          Um VPS que nao vem por API de provedor. Depois, instale o agente nele pela aba Contas.
        </p>
        <div className="mt-5 space-y-4">
          <Field label="Hostname" hint="Como o servidor aparece nas telas.">
            <Input value={hostname} onChange={(e) => setHostname(e.target.value)} placeholder="vps-fiscal-01" autoFocus />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Sistema">
              <select
                value={os}
                onChange={(e) => setOs(e.target.value as typeof os)}
                className="w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
              >
                <option value="linux">Linux</option>
                <option value="windows">Windows</option>
                <option value="unknown">Nao sei</option>
              </select>
            </Field>
            <Field label="Ambiente">
              <select
                value={environment}
                onChange={(e) => setEnvironment(e.target.value as typeof environment)}
                className="w-full rounded border border-line bg-panel px-3 py-2 text-sm text-ink focus:border-accent focus:outline-none"
              >
                <option value="producao">Producao</option>
                <option value="homologacao">Homologacao</option>
                <option value="desenvolvimento">Desenvolvimento</option>
                <option value="outro">Outro</option>
              </select>
            </Field>
          </div>
          <Field label="IP publico" hint="Opcional — so para voce identificar.">
            <Input value={publicIp} onChange={(e) => setPublicIp(e.target.value)} placeholder="203.0.113.10" />
          </Field>
          {erro && <Alert>{erro}</Alert>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={salvar} disabled={hostname.trim().length < 1 || busy}>
            {busy ? 'Salvando...' : 'Cadastrar'}
          </Button>
        </div>
      </div>
    </div>
  )
}
